import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatLuxonDateModule, provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { ErrorStateMatcher, MAT_DATE_FORMATS, MAT_DATE_LOCALE, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmDialogComponent } from '../../dialogs/confirm-dialog/confirm-dialog.component';
import { ProjectRole } from '../../../models/enums';
import { Project } from '../../../models/project';
import { LastUpdatePipe } from '../../../pipes/last-update.pipe';
import { ProjectService, ProjectSimilarityCheckPayload } from '../../../services/project.service';
import { ReferenceDataService } from '../../../services/reference-data.service';
import { MY_DATE_FORMATS, normalizeDate } from '../../../utils/utils';
import { AdvancedChipSelectComponent } from '../../misc/advanced-chip-select/advanced-chip-select.component';
import { separatorKeysCodes } from '../../misc/nested-options';
import { combineLatest, debounceTime, delay, distinctUntilChanged, filter, map, of, skip, startWith } from 'rxjs';
import { rxResource, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SimilarityListPipe } from '../../../pipes/similarity-list.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SimilarityList } from '../../../models/similarity-list';
import { HistoryPanelComponent } from '../../misc/history-panel/history-panel.component';
import { O365AuthService } from '../../../services/o365-auth.service';

export class SchemesErrorStateMatcher implements ErrorStateMatcher {
  constructor(private form?: FormGroup) { }

  isErrorState(): boolean {
    const control = this.form?.get('schemes');
    const invalid = !!(control && control.invalid && (control.touched || control.dirty));
    return invalid;
  }
}

export class TopicsErrorStateMatcher implements ErrorStateMatcher {
  constructor(private form?: FormGroup) { }

  isErrorState(): boolean {
    const control = this.form?.get('topics');
    const invalid = !!(control && control.invalid && (control.touched || control.dirty));
    return invalid;
  }
}


@Component({
  selector: 'app-edit-project',
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    MatLuxonDateModule,
    MatDialogModule,
    MatDatepickerModule,
    MatOptionModule,
    MatButtonModule,
    MatSelectModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSnackBarModule,
    LastUpdatePipe,
    AdvancedChipSelectComponent,
    SimilarityListPipe,
    MatProgressSpinnerModule,
    HistoryPanelComponent
  ],
  providers: [
    provideLuxonDateAdapter(),
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'fr' },
  ],
  templateUrl: './edit-project.component.html',
  styleUrl: './edit-project.component.scss'
})
export class EditProjectComponent implements OnInit {
  // Need to implement ControlValueAccessor so that it can get integrated as a Form Control

  readonly _snackBar = inject(MatSnackBar);
  readonly separatorKeysCodes = separatorKeysCodes;
  form: FormGroup;

  mode: string = 'Create'; // Create, Update or Display
  project: Project = new Project();
  resetCounter = 0; // used to reset the filter form

  private destroyRef = inject(DestroyRef);
  readonly refDataService = inject(ReferenceDataService);

  readonly authService = inject(O365AuthService);

  readonly euficRoleOptions = [
    { value: 'COORDINATOR', label: ProjectRole.COORDINATOR },
    { value: 'PARTNER', label: ProjectRole.PARTNER },
    { value: 'OTHER', label: ProjectRole.OTHER },
  ];

  readonly allTopics = this.refDataService.getGeneralTopicsOfInterest();
  readonly allTopicOptions = computed(() => this.allTopics());

  readonly allUsers = computed(() => {
    // ... implies making a copy and working on the copy
    return [...this.refDataService.getGraphUsers()()].sort((a, b) =>
      a.displayName.localeCompare(b.displayName)
    );
  });

  private checkDuplicateParams = signal<ProjectSimilarityCheckPayload | undefined>(undefined);

  duplicateEntryResource = rxResource<SimilarityList<any> | null, ProjectSimilarityCheckPayload | undefined>({
    params: this.checkDuplicateParams,
    stream: ({ params }) => {
      if (!params) {
        return of(null);
      }
      return this.projectService.checkForSimilarProjects(params).pipe(delay(250));;
    },
  });

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private dialog: MatDialog,) {
    this.form = {} as FormGroup;
  }

  ngOnInit(): void {
    this.buildForm();
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam === null) {
      this.mode = 'Create';
      this.initEmptyForm();
    } else {
      const projectId = Number(idParam);
      if (!idParam || isNaN(projectId)) {
        this.router.navigate(['/projects'], {
          state: { message: '❌ No such project: ' + idParam }
        });
        return;
      }
      this.mode = 'Update';
      this.projectService.getProjectById(projectId).subscribe({
        next: (project: Project) => {
          this.project = project;
          this.form.patchValue(project);
          this.form.get('shortName')?.setValue(project.name);
        },
        error: (err: string) => {
          // handle error (e.g. show toast, redirect, etc.)
          console.error('Failed to load project:', err);
          this.router.navigate(['/projects'],
            {
              state: { message: '❌ Failed to load project: ' + projectId }
            }
          );
        }
      });
    }
  }

  initEmptyForm(): void {
    this.project = new Project();
  }

  cancel(): void {
    this.router.navigate(['/projects']);
  }

  save(): void {
    console.log('Save project');
    if (this.form.valid) {
      const updatedProject: Project = {
        ...this.project,
        ...this.form.value,
          name: this.form.value.shortName,
      };
      let rawValue = this.form.get('startDate')?.value;
      if (rawValue) {
        let isoDate = normalizeDate(rawValue);
        if (!isoDate) {
          this._snackBar.open('⚠️ Invalid project start date', 'Close', { duration: 3000 });
          return;
        }
        updatedProject.startDate = isoDate
      } else {
        updatedProject.startDate = null
      }


      rawValue = this.form.get('endDate')?.value;
      if (rawValue) {
        let isoDate = normalizeDate(rawValue);
        if (!isoDate) {
          this._snackBar.open('⚠️ Invalid project end date', 'Close', { duration: 3000 });
          return;
        }
        updatedProject.endDate = isoDate;
      } else {
        updatedProject.endDate = null;
      }

      if (this.mode === 'Create') {
        console.log('new project: ', updatedProject);
        this.projectService.create(updatedProject).subscribe({
          next: () => {
            this.router.navigate(['/projects'],
              {
                state: { message: '✅ Project created' }
              }
            );
          },
          error: (err) => {
            console.debug('error creating project', err)
            this._snackBar.open('❌ Error creating project', 'Close', { duration: 3000 });
          }
        })
      } else {
        console.log('updatedProject: ', updatedProject);
        this.projectService.update(updatedProject).subscribe({
          next: () => {
            this.router.navigate(['/projects'],
              {
                state: { message: '✅ Project updated' }
              }
            );
          },
          error: (err) => {
            console.debug('error updating project', err)
            this._snackBar.open('❌ Error updating project', 'Close', { duration: 3000 });
          }
        })
      }
    }
  }

  delete(): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Project',
        message: 'Are you sure you want to delete this project?'
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.projectService.delete(this.project!.id).subscribe({
          next: () => {
            this.router.navigate(['/projects'],
              {
                state: { message: '✅ Project deleted' }
              }
            );
          },
          error: (err) => {
            console.debug('error deleting project', err)
            this._snackBar.open(`❌ Error deleting project: ${err}`, 'Close', { duration: 3000 });
          }
        })
      }
    });
  }

  private buildForm(): void {
    this.form = this.fb.group({
      shortName: [this.project?.name || '', Validators.required],
      fullName: [this.project?.fullName || '', Validators.required],
      startDate: [this.project?.startDate || ''],
      endDate: [this.project?.endDate || ''],
      euficRole: [this.project?.euficRole || '', Validators.required],
      website: [this.project?.website || ''],
      topics: [],
    });

    combineLatest([
      this.form.get('shortName')!.valueChanges.pipe(
        startWith('')),
      this.form.get('fullName')!.valueChanges.pipe(
        startWith(''))
    ]).pipe(
      skip(1),
      map(([shortName, fullName]) => ({
        id: this.project.id,
        shortName: (shortName ?? '').trim(),
        fullName: (fullName ?? '').trim()
      }) as ProjectSimilarityCheckPayload),
      filter(payload => payload.shortName !== '' || payload.fullName !== ''),
      debounceTime(300),
      distinctUntilChanged((prev, curr) =>
        prev.shortName === curr.shortName && prev.fullName === curr.fullName
      ),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(payload => {
      this.checkDuplicateParams.set(payload);
    });
  }


  openLink(): void {
    let url = this.form.get('website')?.value
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      window.open(url, '_blank')
    }
  }

  copyLink(): void {
    console.log(this.form, this.form.value)
    let url = this.form.get('website')?.value
    if (url) {
      if (!/^https?:\/\//i.test(url)) {
        url = 'https://' + url;
      }
      navigator.clipboard.writeText(url).then(() => {
        console.log('Copied to clipboard!');
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    }
  }
}
